import React, { forwardRef, Ref } from 'react';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import { IHighlightDates } from '../../Builder/DatePickerWidget';
import styled, { css } from 'styled-components';
import { globalFormat } from '../../../utils/formatting';
import { displayDateFormatDictionary } from './utils';
import { colors } from '../../../config/styles';

interface IProps
  extends Pick<ReactDatePickerProps, 'minDate' | 'maxDate' | 'disabled'> {
  placeholderText?: string;
  selectedValue: Date | null;
  dateFormat?: string;
  onChange: (date: Date | null) => void;
  autoFocus?: boolean;
  hasError?: boolean;
  highlightDates?: IHighlightDates[];
  liveDate?: Date;
}

const Wrapper = styled.div<{ $hasError?: boolean }>`
  ${({ $hasError }) =>
    $hasError &&
    css`
      .react-datepicker__input-container input {
        border-color: ${colors.mainError};
      }
    `}
  .react-datepicker-popper {
    margin-top: 16px; /* Adds space between input and dropdown for drag/drop */
  }
`;

export default forwardRef(
  (
    {
      placeholderText = 'Date',
      selectedValue,
      onChange,
      dateFormat,
      autoFocus = false,
      highlightDates = undefined,
      liveDate = undefined,
      hasError,
      maxDate,
      minDate,
      disabled,
    }: IProps,
    ref: Ref<ReactDatePicker>,
  ) => {
    const { dateFormat: globalDateFormat } = globalFormat.getGlobalFormat();
    const globalDatePickerFormat =
      displayDateFormatDictionary[globalDateFormat].DATE;

    return (
      <Wrapper $hasError={hasError}>
        <ReactDatePicker
          ref={ref}
          placeholderText={placeholderText}
          selected={selectedValue}
          strictParsing
          dateFormat={dateFormat ?? globalDatePickerFormat}
          onChange={onChange}
          disabledKeyboardNavigation={true}
          showDisabledMonthNavigation={true}
          autoFocus={autoFocus}
          formatWeekDay={(weekdayStr) => weekdayStr.charAt(0)}
          highlightDates={highlightDates}
          openToDate={liveDate}
          maxDate={maxDate}
          minDate={minDate}
          disabled={disabled}
          popperPlacement={'bottom-start'}
          showPopperArrow={false}
          popperProps={{
            strategy: 'absolute',
          }}
          popperModifiers={[
            {
              name: 'preventOverflow',
              enabled: false,
            },
          ]}
        />
      </Wrapper>
    );
  },
);
